"use client";
import React from "react";
import { useRouter, usePathname, notFound } from "next/navigation";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import dynamic from "next/dynamic";

const Player = dynamic(() => import("@lottiefiles/react-lottie-player").then((mod) => mod.Player), { ssr: false, });
import NextImage from "next/image";
import Typography from "@mui/material/Typography";
import Link from "@AdbhutByte/utils/components/Link";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Button from "@mui/material/Button";
import Zoom from "@mui/material/Zoom";
import Stack from "@mui/material/Stack";
import { enqueueSnackbar } from "notistack";
import { HotKeys, configure } from "react-hotkeys";
import getProfilesToFollow, {
  profileProps,
} from "@AdbhutByte/utils/lib/getProfilesToFollow";
import {
  SITE_FALLBACK_NAME,
  SITE_FALLBACK_URL,
} from "@AdbhutByte/utils/universalModifiers";

configure({
  ignoreTags: ["select", "textarea"],
});

const baseUrl = process.env.NEXT_PUBLIC_URL ?? SITE_FALLBACK_URL;
const baseName = process.env.NEXT_PUBLIC_SITE_NAME ?? SITE_FALLBACK_NAME;

const MuiPlayer = styled(Player)({});
const Image = styled(NextImage)({});

interface FollowRedirectProps {
  to: string;
}

export async function generateStaticParams() {
  const profileToFollow = await getProfilesToFollow();

  return (profileToFollow as profileProps[]).map((profile) => ({
    to: profile.name,
  }));
}

const FollowRedirect = ({ to }: FollowRedirectProps) => {
  to = to.toLocaleLowerCase();
  const router = useRouter();
  const pathname = usePathname();
  const [prefix, setPrefix] = React.useState<string | false>(false);
  const [username, setUsername] = React.useState<string | false>(false);
  const [brand, setBrand] = React.useState<string | false>(false);
  const [icon, setIcon] = React.useState<string | false>(false);
  const suffix = "ref=" + new URL(baseUrl).host + "&utm_source=" + baseName;
  const [linkCopied, setLinkCopied] = React.useState(false);
  const [copyMethod, setCopyMethod] = React.useState<
    "button" | "keyboard" | "context"
  >("context");
  const [remainingToRedirect, setRemainingToRedirect] =
    React.useState<number>(5);
  const linkInputRef = React.useRef<HTMLInputElement>(null);

  const handleLinkCopyNotiClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setLinkCopied(false);
    setCopyMethod("context");
  };
  const handleLinkCopy = () => {
    setCopyMethod("button");
    setLinkCopied(false);
    if (!linkInputRef.current) return;
    const linkInputValue = linkInputRef.current.value;
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(linkInputValue)
        .then(() => {
          setLinkCopied(true);
          enqueueSnackbar("Copied!", {
            onClose: () => handleLinkCopyNotiClose,
            variant: "info",
          });
        })
        .catch(() => {
          setLinkCopied(false);
          enqueueSnackbar("Error while coping link", {
            onClose: () => handleLinkCopyNotiClose,
            variant: "error",
          });
        });
    } else {
      setLinkCopied(false);
      fallbackHandleLinkCopy(linkInputValue);
    }
  };
  const fallbackHandleLinkCopy = (fullLink: string) => {
    setCopyMethod("button");
    setLinkCopied(false);
    const textArea = document.createElement("textarea");
    textArea.value = fullLink;

    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.opacity = "0";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand("copy");
      setLinkCopied(true);
      enqueueSnackbar("Copied!", {
        onClose: () => handleLinkCopyNotiClose,
        variant: "info",
      });
    } catch (error) {
      setLinkCopied(false);
      enqueueSnackbar("Error while coping link", {
        onClose: () => handleLinkCopyNotiClose,
        variant: "error",
      });
    }

    document.body.removeChild(textArea);
  };
  const handleLinkSelect = () => {
    if (!linkInputRef.current) return;
    linkInputRef.current.select();
  };
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const profile = await getProfilesToFollow(to); // Call the async function using await
        if (!profile) {
          notFound();
        } else {
          setPrefix((profile as profileProps).prefix);
          setBrand((profile as profileProps).brand_name);
          setUsername((profile as profileProps).username);
          setIcon((profile as profileProps).icon);
        }
      } catch (error) {
        notFound();
      }
    };

    if (!prefix && !username && !icon) fetchData();
  }, [icon, prefix, to, username]);
  React.useEffect(() => {
    if (prefix && username && icon) {
      const redirectUrl = `${prefix as string}${username as string}/?${suffix}`;
      const remaining = setInterval(() => {
        setRemainingToRedirect((prev) => {
          const nextValue = prev - 1;
          return nextValue >= 0 ? nextValue : 0;
        });
      }, 1000);
      const timer = setTimeout(() => {
        window.location.replace(redirectUrl);
      }, 7000);
      return () => {
        clearTimeout(timer);
        clearInterval(remaining);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [icon, prefix, router, username, to, router, pathname]);

  const copyKeyMaps = {
    COPY_CONFIRM: ["ctrl+c", "command+c"],
  };

  const copyKeyMapHandlers = {
    COPY_CONFIRM: (event: any) => {
      setCopyMethod("keyboard");
      const inputElement = linkInputRef.current;
      const selectedText = inputElement?.value.substring(
        inputElement.selectionStart as number,
        inputElement.selectionEnd as number
      );
      if (
        selectedText?.trim() ===
        `${prefix as string}${username as string}/?${suffix}`.trim()
      ) {
        enqueueSnackbar("Copied!", {
          onClose: () => handleLinkCopyNotiClose,
          variant: "info",
        });
      }
    },
  };
  React.useEffect(() => {
    function handleCopyEvent(event: ClipboardEvent) {
      const inputElement = linkInputRef.current;
      const selectedText = inputElement?.value.substring(
        inputElement.selectionStart as number,
        inputElement.selectionEnd as number
      );
      if (
        selectedText?.trim() ===
        `${prefix as string}${username as string}/?${suffix}`.trim()
      ) {
        if (copyMethod === "context") {
          enqueueSnackbar("Copied!", {
            onClose: () => handleLinkCopyNotiClose,
            variant: "info",
          });
        }
      }
    }

    document.addEventListener("copy", handleCopyEvent);

    return () => {
      document.removeEventListener("copy", handleCopyEvent);
    };
  }, [copyMethod, prefix, suffix, username]);
  return (
    <HotKeys keyMap={copyKeyMaps} handlers={copyKeyMapHandlers}>
      <Container maxWidth={false}>
        <Box
          minHeight="100vh"
          component="section"
          id="redirectionMain"
          className="redirectionMain"
          py={5}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          {prefix && username && icon && (
            <Box sx={{ width: "100%" }}>
              <Stack
                display="flex"
                justifyContent="center"
                alignItems="center"
                direction="row"
                spacing={2}
              >
                <Box>
                  <Image
                    src="/reserved/logo_and_favs/mrasadatik_logo@1000.png"
                    alt="mrasadatik"
                    width={50}
                    height={50}
                    sx={{ textAlign: "center", objectFit: "contain" }}
                  />
                </Box>
                <Box>
                  <MuiPlayer
                    autoplay={true}
                    loop={true}
                    src="/reserved/lotties/follow/redirect.json"
                    sx={{ height: 50, width: 50, textAlign: "center" }}
                  />
                </Box>
                <Box
                  component={Link}
                  href={`${prefix as string}${username as string}/?${suffix}`}
                  target="_blank"
                  sx={{ m: 0 }}
                >
                  <Image
                    src={icon as string}
                    alt={baseName + " on " + brand}
                    width={50}
                    height={50}
                    sx={{ textAlign: "center", objectFit: "contain" }}
                  />
                </Box>
              </Stack>
              <Zoom in={remainingToRedirect > 0} unmountOnExit>
                <Box textAlign="center" pt={2}>
                  <Typography
                    component="p"
                    color="text.secondary"
                    variant="caption"
                  >
                    Redirecting within {remainingToRedirect} second(s)!
                  </Typography>
                </Box>
              </Zoom>
              <Zoom in={remainingToRedirect == 0} unmountOnExit>
                <Box textAlign="center" pt={2}>
                  <Typography
                    component="p"
                    color="text.secondary"
                    variant="caption"
                  >
                    Redirection not happened?{` `}
                    <Link
                      color="inherit"
                      href={`${prefix as string}${username as string
                        }/?${suffix}`}
                      target="_blank"
                    >
                      Try this
                    </Link>
                  </Typography>
                  <Typography
                    component="p"
                    color="text.secondary"
                    variant="caption"
                    py={2}
                  >
                    or,
                  </Typography>
                  <Box component="form" noValidate autoComplete="off">
                    <TextField
                      label="Copy link"
                      id="copy_link"
                      name="copy_link"
                      inputRef={linkInputRef}
                      size="small"
                      value={`${prefix as string}${username as string
                        }/?${suffix}`}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      onClick={handleLinkSelect}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              type="button"
                              id="copy_link_button"
                              name="copy_link_button"
                              size="small"
                              color="secondary"
                              onClick={handleLinkCopy}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>
              </Zoom>
            </Box>
          )}
        </Box>
      </Container>
    </HotKeys>
  );
};

export default FollowRedirect;
